import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Text,
  Loader,
  Alert,
  Paper,
  Tabs,
  Card,
  Group,
  Stack,
  Anchor,
  Table,
  ScrollArea,
  ActionIcon,
  TextInput,
  Grid,
} from '@mantine/core';
import { IconSearch, IconDownload, IconPrinter, IconInfoCircle } from '@tabler/icons-react';
import { useParams, useNavigate } from 'react-router-dom';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';
import MembershipPopup from '../MembershipPopup';
import SQLEditorPopup from '../../supply-chain/purchasing/SQLEditorPopup';

const ColorLegend = () => (
  <Card withBorder radius="md" style={{ width: '350px' }}>
    <Text size="sm" fw={500} mb="xs">
      Legend
    </Text>
    <Stack gap="xs">
      {[
        { color: '#73A57D', label: '-2% or more' },
        { color: '#D1A752', label: '-2% - -5%' },
        { color: '#D88C8C', label: 'Less than -5%' },
      ].map(({ color, label }) => (
        <Group key={label} gap="xs" align="flex-start">
          <Box
            style={{
              width: '16px',
              height: '16px',
              backgroundColor: color,
              borderRadius: '2px',
              flexShrink: 0,
              marginTop: '2px',
            }}
          />
          <Text size="xs" c="dimmed" style={{ flex: 1, lineHeight: 1.3 }}>
            {label}
          </Text>
        </Group>
      ))}
    </Stack>
  </Card>
);

const SalesPerformanceScorecardPricingDetail = () => {
  const { ccode, mode } = useParams();
  const navigate = useNavigate();

  // State for pop-ups and search
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  const [openDetailsQuery, setOpenDetailsQuery] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [detailSearchTerm, setDetailSearchTerm] = useState('');

  // Clear handlers for proper dialog closing
  const handleCloseMembership = () => setOpenMembership(false);
  const handleCloseSummaryQuery = () => setOpenSummaryQuery(false);
  const handleCloseDetailsQuery = () => setOpenDetailsQuery(false);

  // Normalize mode to lower case.
  const normalizedMode = mode.toLowerCase();

  // Endpoints for the three modes.
  const mtdEndpoint = `${config.apiBaseUrl}/sales/scorecard/${ccode}/pricingdetails/mtd`;
  const summaryEndpoint = `${config.apiBaseUrl}/sales/scorecard/${ccode}/pricing${normalizedMode.toUpperCase()}Summary`;

  const { data: salesPersonName, isLoading: isLoadingName, error: errorName } = useAuthenticatedData(`${config.apiBaseUrl}/data/name?ccode=${ccode}`);

  const calculateDetailTotals = (details) => {
    if (!details || details.length === 0) return null;

    const totals = {
      id: 'TOTAL',
      accountName: 'TOTAL',
      qty: 0,
      unitPrice: 0,
      discountAmount: 0,
      extendedSalePrice: 0,
      pentagonSellPrice: 0,
      amountDifference: 0,
      percentDifference: 0,
    };

    details.forEach(row => {
      totals.qty += row.qty || 0;
      totals.discountAmount += row.discountAmount || 0;
      totals.extendedSalePrice += row.extendedSalePrice || 0;
      totals.pentagonSellPrice += row.pentagonSellPrice || 0;
      totals.amountDifference += row.amountDifference || 0;
    });

    totals.percentDifference = totals.pentagonSellPrice ? (totals.extendedSalePrice / totals.pentagonSellPrice) * 100 : 0;
    totals.percentDifference = totals.percentDifference - 100;

    return totals;
  };

  // For MTD, we only need the pricing detail endpoint.
  const {
    data: mtdData,
    isLoading: mtdLoading,
    error: mtdError
  } = useAuthenticatedData(normalizedMode === 'mtd' ? mtdEndpoint : null);

  // For QTD or YTD, first load the summary data.
  const {
    data: summaryData,
    isLoading: summaryLoading,
    error: summaryError
  } = useAuthenticatedData(
    normalizedMode !== 'mtd' ? summaryEndpoint : null
  );

  // State for selected summary row (for QTD/YTD).
  const [selectedSummary, setSelectedSummary] = useState(null);

  // Build a detail endpoint URL when a summary row is selected.
  const detailEndpoint =
    selectedSummary &&
    `${config.apiBaseUrl}/sales/scorecard/${ccode}/pricing${normalizedMode.toUpperCase()}/detail?fromDate=${encodeURIComponent(
      selectedSummary.start
    )}&toDate=${encodeURIComponent(selectedSummary.end)}`;

  // Load detail data (for QTD and YTD) when a summary row is selected.
  const {
    data: detailData,
    isLoading: detailLoading,
    error: detailError
  } = useAuthenticatedData(normalizedMode !== 'mtd' ? detailEndpoint : null, {
    // Refetch on change of detailEndpoint.
    deps: [detailEndpoint]
  });

  // Handler for tab change (navigates to a different mode)
  const handleTabChange = (value) => {
    navigate(`/sales/salesperson/${ccode}/scorecard/pricing/${value}`);
  };

  // Format functions
  const formatCurrency = (value) => {
    if (!value || value === 0) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '';
    return `${value.toFixed(2)}%`;
  };

  const formatDate = (value) => {
    return value ? new Date(value).toLocaleDateString() : '';
  };

  const getRowColor = (colorCategory, isTotal = false) => {
    if (isTotal) return '#e0e0e0';
    switch (colorCategory?.toLowerCase()) {
      case 'red': return '#D88C8C';
      case 'green': return '#73A57D';
      case 'yellow': return '#D1A752';
      default: return undefined;
    }
  };

  // Filtered data for MTD
  const filteredMtdData = useMemo(() => {
    if (!mtdData || !searchTerm) return mtdData || [];
    return mtdData.filter(row =>
      row.accountName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.partnumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.marketSegment?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [mtdData, searchTerm]);

  // Filtered data for summary
  const filteredSummaryData = useMemo(() => {
    if (!summaryData || !searchTerm) return summaryData || [];
    return summaryData.filter(row =>
      formatDate(row.start).includes(searchTerm) ||
      formatDate(row.end).includes(searchTerm)
    );
  }, [summaryData, searchTerm]);

  // Filtered data for details
  const filteredDetailData = useMemo(() => {
    if (!detailData || !detailSearchTerm) return detailData || [];
    return detailData.filter(row =>
      row.accountName?.toLowerCase().includes(detailSearchTerm.toLowerCase()) ||
      row.partnumber?.toLowerCase().includes(detailSearchTerm.toLowerCase()) ||
      row.marketSegment?.toLowerCase().includes(detailSearchTerm.toLowerCase())
    );
  }, [detailData, detailSearchTerm]);

  const exportToCSV = (data, filename) => {
    if (!data || !data.length) return;

    const headers = normalizedMode === 'mtd' || selectedSummary
      ? ['Account', 'Market Segment', 'Date', 'Partnumber', 'Qty', 'Unit Price', 'Discount', 'Extended Price', 'Pentagon Price', 'Amount Diff', 'Percent Diff', 'Condition', 'Class', 'Physical WH', 'Virtual WH']
      : ['From', 'To', 'Avg. % Diff', 'Total Amount Diff'];

    const csvContent = [
      headers.join(','),
      ...data.map(row => {
        if (normalizedMode === 'mtd' || selectedSummary) {
          return [
            `"${row.accountName || ''}"`,
            `"${row.marketSegment || ''}"`,
            `"${formatDate(row.date)}"`,
            `"${row.partnumber || ''}"`,
            row.qty || 0,
            row.unitPrice || 0,
            row.discountAmount || 0,
            row.extendedSalePrice || 0,
            row.pentagonSellPrice || 0,
            row.amountDifference || 0,
            row.percentDifference || 0,
            `"${row.condition || ''}"`,
            `"${row.class || ''}"`,
            `"${row.physicalWarehouseCode || ''}"`,
            `"${row.virtualWarehouseCode || ''}"`
          ].join(',');
        } else {
          return [
            `"${formatDate(row.start)}"`,
            `"${formatDate(row.end)}"`,
            row.avgPercentDifference || 0,
            row.totalAmtDifference || 0
          ].join(',');
        }
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Render loading and error states
  if (normalizedMode === 'mtd' && mtdLoading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  if (normalizedMode === 'mtd' && mtdError) {
    return (
      <Alert color="red" title="Error">
        {mtdError.message}
      </Alert>
    );
  }

  if (normalizedMode !== 'mtd' && summaryLoading) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  if (normalizedMode !== 'mtd' && summaryError) {
    return (
      <Alert color="red" title="Error">
        {summaryError.message}
      </Alert>
    );
  }

  if (normalizedMode !== 'mtd' && !summaryData) {
    return (
      <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader />
      </Box>
    );
  }

  return (
    <Paper shadow="md" radius="md">
      <Stack gap="xs">
        {/* Header Section with Title and Helper Links */}
        <Box>
          <Text
            component="h1"
            style={{
              fontFamily: 'Orbitron, sans-serif',
              color: '#FDB813',
              fontStyle: 'italic',
              fontSize: '1.5rem',
              marginBottom: 0,
              textAlign: 'left'
            }}
          >
            Pricing {normalizedMode.toUpperCase()} - {salesPersonName}
          </Text>

          {/* Technical options: small links */}
          <Group gap="xs" mb="sm">
            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-individual-pricing-mtd',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Summary Query
            </Anchor>
            <Anchor
              size="xs"
              c="dimmed"
              onClick={() =>
                window.open(
                  '/query-commander/editor?queryKey=sales-pricing-detail-by-week',
                  '_blank'
                )
              }
              style={{ cursor: 'pointer' }}
            >
              Details Query
            </Anchor>
          </Group>
        </Box>

        {/* Info Cards - tighter spacing */}
        <Group gap="xs" align="stretch" style={{ marginBottom: '0.5rem' }}>
          <Box style={{ flex: 1, minWidth: '300px' }}>
            <Card withBorder radius="md" style={{ height: '100%' }}>
              <Text size="sm" fw={500} mb="xs">
                About the Data
              </Text>
              <Text size="xs" c="dimmed">
                These are invoices sold by {salesPersonName}, regardless of who the customer was for all A & B parts.
                This shows the pricing for each transaction and the difference between the Pentagon price and the actual sale price.
              </Text>
            </Card>
          </Box>
          <ColorLegend />
        </Group>

        {/* Tabs - compact */}
        <Tabs
          value={normalizedMode}
          onChange={handleTabChange}
          styles={(theme) => ({
            tab: {
              color: theme.colorScheme === 'dark' ? '#b3b3b3' : '#666666',
              '&[data-active]': {
                color: theme.colorScheme === 'dark' ? '#ffffff' : '#000000',
                fontWeight: 'bold',
              },
              padding: '6px 12px',
            },
          })}
        >
          <Tabs.List>
            <Tabs.Tab value="mtd">Month-to-Date</Tabs.Tab>
            <Tabs.Tab value="qtd">Quarter-to-Date</Tabs.Tab>
            <Tabs.Tab value="ytd">Year-to-Date</Tabs.Tab>
          </Tabs.List>
        </Tabs>

        {normalizedMode === 'mtd' ? (
          // MTD: Show a single detail table
          <>
            {/* Toolbar */}
            <Group justify="space-between" mb="xs">
              <TextInput
                placeholder="Search accounts, parts, or segments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftSection={<IconSearch size={16} />}
                style={{ width: '350px' }}
                size="sm"
              />
              <Group gap="xs">
                <ActionIcon
                  variant="default"
                  onClick={() => exportToCSV(filteredMtdData, `pricing-${normalizedMode}`)}
                  title="Export to CSV"
                  size="sm"
                >
                  <IconDownload size={16} />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  onClick={() => window.print()}
                  title="Print"
                  size="sm"
                >
                  <IconPrinter size={16} />
                </ActionIcon>
              </Group>
            </Group>

            {/* Data Table */}
            <Paper withBorder style={{ height: '500px' }}>
              <ScrollArea style={{ height: '100%' }}>
                <Table striped highlightOnHover size="sm">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: '200px' }}>Account</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Market Segment</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Date</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Partnumber</Table.Th>
                      <Table.Th style={{ width: '60px' }}>Qty</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Unit Price</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Discount</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Extended Price</Table.Th>
                      <Table.Th style={{ width: '120px' }}>Pentagon Price</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Amount Diff</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Percent Diff</Table.Th>
                      <Table.Th style={{ width: '80px' }}>Condition</Table.Th>
                      <Table.Th style={{ width: '80px' }}>Class</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Physical WH</Table.Th>
                      <Table.Th style={{ width: '100px' }}>Virtual WH</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {[...filteredMtdData, calculateDetailTotals(filteredMtdData)].filter(Boolean).map((row) => (
                      <Table.Tr
                        key={row.id}
                        style={{
                          backgroundColor: getRowColor(row.colorCategory, row.id === 'TOTAL'),
                          fontWeight: row.id === 'TOTAL' ? 'bold' : 'normal',
                        }}
                      >
                        <Table.Td><Text size="xs" fw={row.id === 'TOTAL' ? 700 : 500} c="dark">{row.accountName}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.marketSegment}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{formatDate(row.date)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.partnumber}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{row.qty}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.unitPrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.discountAmount)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.extendedSalePrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.pentagonSellPrice)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.amountDifference)}</Text></Table.Td>
                        <Table.Td><Text size="xs" fw={600} c="dark">{formatPercent(row.percentDifference)}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.condition}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.class}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.physicalWarehouseCode}</Text></Table.Td>
                        <Table.Td><Text size="xs" c="dark">{row.virtualWarehouseCode}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            </Paper>

            <Text size="xs" c="dimmed">
              Showing {filteredMtdData.length} result{filteredMtdData.length !== 1 ? 's' : ''}
            </Text>
          </>
        ) : (
          // QTD and YTD: Show a two-pane layout with summary and detail tables
          <Grid gutter="xs">
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Text size="md" fw={600} mb="xs">
                {normalizedMode === 'qtd' ? 'Weekly Summary' : 'Monthly Summary'}
              </Text>

              {/* Summary Toolbar */}
              <Group justify="space-between" mb="xs">
                <TextInput
                  placeholder="Search dates..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  leftSection={<IconSearch size={16} />}
                  style={{ width: '200px' }}
                  size="sm"
                />
                <ActionIcon
                  variant="default"
                  onClick={() => exportToCSV(filteredSummaryData, `pricing-${normalizedMode}-summary`)}
                  title="Export to CSV"
                  size="sm"
                >
                  <IconDownload size={16} />
                </ActionIcon>
              </Group>

              {/* Summary Table */}
              <Paper withBorder style={{ height: '350px' }}>
                <ScrollArea style={{ height: '100%' }}>
                  <Table striped highlightOnHover size="sm">
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th style={{ width: '80px' }}>From</Table.Th>
                        <Table.Th style={{ width: '80px' }}>To</Table.Th>
                        <Table.Th style={{ width: '100px' }}>Avg. % Diff</Table.Th>
                        <Table.Th style={{ width: '120px' }}>Total Amount Diff</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {filteredSummaryData.map((row) => (
                        <Table.Tr
                          key={row.id}
                          style={{
                            backgroundColor: getRowColor(row.rowColor),
                            cursor: 'pointer',
                          }}
                          onClick={() => setSelectedSummary(row)}
                        >
                          <Table.Td><Text size="xs" fw={500} c="dark">{formatDate(row.start)}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={500} c="dark">{formatDate(row.end)}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={600} c="dark">{formatPercent(row.avgPercentDifference)}</Text></Table.Td>
                          <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.totalAmtDifference)}</Text></Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              </Paper>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 8 }}>
              <Group justify="space-between" align="center" mb="xs">
                <Text size="md" fw={600}>
                  Transaction Details
                  {selectedSummary && (
                    <Text size="sm" fw={400} c="dimmed" ml="xs" component="span">
                      ({formatDate(selectedSummary.start)} - {formatDate(selectedSummary.end)})
                    </Text>
                  )}
                </Text>
              </Group>

              {selectedSummary ? (
                detailLoading ? (
                  <Box style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
                    <Loader />
                  </Box>
                ) : detailError ? (
                  <Alert color="red" title="Error">
                    {detailError.message}
                  </Alert>
                ) : (
                  <>
                    {/* Detail Toolbar */}
                    <Group justify="space-between" mb="xs">
                      <TextInput
                        placeholder="Search details..."
                        value={detailSearchTerm}
                        onChange={(e) => setDetailSearchTerm(e.target.value)}
                        leftSection={<IconSearch size={16} />}
                        style={{ width: '250px' }}
                        size="sm"
                      />
                      <ActionIcon
                        variant="default"
                        onClick={() => exportToCSV(filteredDetailData, `pricing-${normalizedMode}-details`)}
                        title="Export to CSV"
                        size="sm"
                      >
                        <IconDownload size={16} />
                      </ActionIcon>
                    </Group>

                    {/* Detail Table */}
                    <Paper withBorder style={{ height: '350px' }}>
                      <ScrollArea style={{ height: '100%' }}>
                        <Table striped highlightOnHover size="sm">
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: '150px' }}>Account</Table.Th>
                              <Table.Th style={{ width: '100px' }}>Market Segment</Table.Th>
                              <Table.Th style={{ width: '80px' }}>Date</Table.Th>
                              <Table.Th style={{ width: '100px' }}>Partnumber</Table.Th>
                              <Table.Th style={{ width: '50px' }}>Qty</Table.Th>
                              <Table.Th style={{ width: '80px' }}>Unit Price</Table.Th>
                              <Table.Th style={{ width: '80px' }}>Extended Price</Table.Th>
                              <Table.Th style={{ width: '80px' }}>Pentagon Price</Table.Th>
                              <Table.Th style={{ width: '80px' }}>Percent Diff</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {[...filteredDetailData, calculateDetailTotals(filteredDetailData)].filter(Boolean).map((row) => (
                              <Table.Tr
                                key={row.id}
                                style={{
                                  backgroundColor: getRowColor(row.colorCategory, row.id === 'TOTAL'),
                                  fontWeight: row.id === 'TOTAL' ? 'bold' : 'normal',
                                }}
                              >
                                <Table.Td><Text size="xs" fw={row.id === 'TOTAL' ? 700 : 500} c="dark">{row.accountName}</Text></Table.Td>
                                <Table.Td><Text size="xs" c="dark">{row.marketSegment}</Text></Table.Td>
                                <Table.Td><Text size="xs" c="dark">{formatDate(row.date)}</Text></Table.Td>
                                <Table.Td><Text size="xs" c="dark">{row.partnumber}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={600} c="dark">{row.qty}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.unitPrice)}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.extendedSalePrice)}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={600} c="dark">{formatCurrency(row.pentagonSellPrice)}</Text></Table.Td>
                                <Table.Td><Text size="xs" fw={600} c="dark">{formatPercent(row.percentDifference)}</Text></Table.Td>
                              </Table.Tr>
                            ))}
                          </Table.Tbody>
                        </Table>
                      </ScrollArea>
                    </Paper>

                    <Text size="xs" c="dimmed">
                      Showing {filteredDetailData.length} result{filteredDetailData.length !== 1 ? 's' : ''}
                    </Text>
                  </>
                )
              ) : (
                <Alert color="blue" icon={<IconInfoCircle size={16} />}>
                  Please click a summary row to view detail transactions.
                </Alert>
              )}
            </Grid.Col>
          </Grid>
        )}
      </Stack>

 
    </Paper>
  );
};

export default SalesPerformanceScorecardPricingDetail;